import {ClearOutlined} from '@ant-design/icons'
import CardTitle from 'common/components/CardTitle.tsx'
import {Alert, Button, Card, Form, Spin} from 'antd'
import { CardTitleWithTooltip } from 'common/components/CardTitleWithTooltip'
import {ReportRequestParametersObjectObject, SavedReportBean} from 'common/api/api.ts'
import {retargetingListApi, savedReportApi} from 'common/api/entitiesApi.tsx'
import {useAppContext} from 'common/context/AppContext.tsx'
import {CommonRoutes} from 'common/context/commonRoutes.ts'
import {useUserContext} from 'common/context/UserContext.tsx'
import {ReportResultTable} from 'common/pages/report/ReportResultTable.tsx'
import {useReportRunner as useReportEngine} from 'common/pages/report/useReportRunner.ts'
import {decodeFilters, FilterPreset} from 'common/util/urlPresets.ts'
import {apiCall, getCopyName, showError} from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import React, {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import {buildReportPayload} from './buildReportPayload.ts'
import {CreateRetargetingListFormValues} from './CreateRetargetingListButton.tsx'
import {ReportForm} from './ReportForm.tsx'
import {resolveDates} from './resolveDates.ts'
import {buildSavedReportBody, getDefaultSelections, mapSavedReportToFormState} from './savedReportMapper.ts'
import SaveReportButton from './SaveReportButton.tsx'
import {FilterKV} from './types.ts'
import {useReportMetadata} from './useReportMetadata.ts'

export const reportPeriodLabels: Record<string, string> = {
    TODAY: 'Today',
    YESTERDAY: 'Yesterday',
    LAST_3_DAYS: 'Last 3 Days',
    LAST_7_DAYS: 'Last 7 Days',
    LAST_28_DAYS: 'Last 28 Days',
    THIS_MONTH: 'This Month',
    LAST_MONTH: 'Last Month',
    CUSTOM_RANGE: 'Custom Range',
}

export interface ReportFormProps {
  mode: 'create' | 'edit' | 'copy'
  autoLoad?: boolean
  reportType?: ReportRequestParametersObjectObject.TypeEnum
  savedReportBaseRoute?: string
  allowCreateRetargetingList?: boolean
  title?: string
}

const Report: React.FC<ReportFormProps> = ({
  mode,
  autoLoad,
  reportType: reportTypeProp,
  savedReportBaseRoute = CommonRoutes.REPORT,
  allowCreateRetargetingList = true,
  title,
}) => {
  const { user } = useUserContext()
  const { messageApi, preloader, setPreloader } = useAppContext()
  const { id } = useParams<{ id?: string }>()
  const idNum = id ? parseInt(id) : 0
  const location = useLocation()
  const navigate = useNavigate()
  const appContext = useAppContext()
  const [loading, setLoading] = useState<boolean>(false)
  const navigateTo = useNavigate()
  const autoLoadRequested = autoLoad || new URLSearchParams(location.search).get('autoLoad') === '1'

    const [selectedFilters, setSelectedFilters] = useState<FilterKV[]>([{dimension: undefined, value: undefined}])
    const [selectedKeyColumns, setSelectedKeyColumns] = useState<string[]>([])
    const [selectedValueColumns, setSelectedValueColumns] = useState<string[]>([])
    const [selectedEventColumns, setSelectedEventColumns] = useState<string[]>([])
    const [name, setName] = useState<string>('')
    const [shouldAutogen, setShouldAutogen] = useState(false)
    const autogenFiredRef = React.useRef(false)
    const [form] = Form.useForm()
    const selectedPeriod = Form.useWatch('period', form) as SavedReportBean.ReportPeriodEnum | undefined
    const selectedTimezone = Form.useWatch('timezone', form) as string | undefined
    const selectedDateRange = Form.useWatch('dateRange', form) as [dayjs.Dayjs, dayjs.Dayjs] | undefined
    const startDate = selectedDateRange?.[0]
    const endDate = selectedDateRange?.[1]
    const [savedReportType, setSavedReportType] = useState<ReportRequestParametersObjectObject.TypeEnum | undefined>(
        undefined,
    )

    const [modalVisible, setModalVisible] = useState(false)

  const {
    state: reportState,
    generate: generateReport,
    onSort: changeSort,
    clearResults,
    exportReport,
    rowObjects,
  } = useReportEngine()
  const reportType = savedReportType ?? reportTypeProp ?? ReportRequestParametersObjectObject.TypeEnum.Stats
  const { metadataLoaded, allFilters, reportKeys, reportValues } = useReportMetadata(reportType, form, user?.timezoneId)

    const filtersPresetToArray = (preset: FilterPreset): FilterKV[] => {
        const acc: FilterKV[] = []
        Object.entries(preset).forEach(([dimension, values]) => {
            values.forEach((value) => {
                acc.push({dimension, value: Number(value) as any})
            })
        })
        return acc.length ? acc : [{dimension: undefined, value: undefined}]
    }

    const applyIncomingPreset = (preset: FilterPreset | null, qs: URLSearchParams) => {
        if (!preset) {
            return
        }

        setSelectedFilters(filtersPresetToArray(preset))

        const period = qs.get('period') as SavedReportBean.ReportPeriodEnum | null
        const tz = qs.get('tz')
        const startStr = qs.get('start')
        const endStr = qs.get('end')

        if (period) {
            form.setFieldValue('period', period)
        }
        if (tz) {
            form.setFieldValue('timezone', tz)
        }

        if (period === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && startStr && endStr) {
            const startDate = dayjs(startStr, 'YYYY-MM-DD')
            const endDate = dayjs(endStr, 'YYYY-MM-DD')
            form.setFieldsValue({
                startDate,
                endDate,
                dateRange: [startDate, endDate],
            })
        }
        const {dimensions: defaultDimensions, metrics: defaultMetrics} = getDefaultSelections(reportType)
        form.setFieldValue('dimensions', defaultDimensions)
        setSelectedKeyColumns(defaultDimensions)
        form.setFieldValue('metrics', defaultMetrics)
        setSelectedValueColumns(defaultMetrics)

        const autogen = qs.get('autogen') === '1'
        if (autogen) {
            autogenFiredRef.current = false
            setShouldAutogen(true)
        }
    }

    useEffect(() => {
        if (mode === 'create') {
            form.resetFields()
            setSelectedKeyColumns([])
            setSelectedValueColumns([])
            setSelectedFilters([{dimension: undefined, value: undefined}])
            setName('')
            setSavedReportType(undefined)

            const tzNow = form.getFieldValue('timezone')
            if (!tzNow && user?.timezoneId) {
                form.setFieldsValue({timezone: user.timezoneId})
            }

            const periodNow = form.getFieldValue('period')
            if (!periodNow) {
                form.setFieldsValue({period: SavedReportBean.ReportPeriodEnum.TODAY})
            }

            clearResults()
        }
    }, [mode])

    useEffect(() => {
        if (reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker) {
            setSelectedValueColumns([])
            form.setFieldValue('metrics', [])
        }
    }, [reportType, form])

    useEffect(() => {
        if (!selectedPeriod || selectedPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE) {
            return
        }

        const timezone = String(selectedTimezone ?? user?.timezoneId ?? 'UTC')
        const {start, end} = resolveDates(selectedPeriod, undefined, undefined, timezone)
        if (!start || !end) {
            return
        }

        const nextStart = dayjs(start, 'YYYY-MM-DD')
        const nextEnd = dayjs(end, 'YYYY-MM-DD')
        const currentRange = form.getFieldValue('dateRange') as [dayjs.Dayjs, dayjs.Dayjs] | undefined
        if (currentRange?.[0]?.isSame(nextStart, 'day') && currentRange?.[1]?.isSame(nextEnd, 'day')) {
            return
        }

        form.setFieldValue('dateRange', [nextStart, nextEnd])
    }, [form, selectedPeriod, selectedTimezone, user?.timezoneId])

    useEffect(() => {
        const qs = new URLSearchParams(location.search)
        const preset = decodeFilters(qs.get('filters'))
        if (preset) {
            applyIncomingPreset(preset, qs)
            if (location.state) {
                navigate('.', {replace: true, state: null})
            }
        }
    }, [location.search])

    useEffect(() => {
        const isLoading = reportState.loading
        if (!(isLoading && preloader)) {
            setPreloader(isLoading)
        }
    }, [reportState.loading, preloader, setPreloader])

    useEffect(() => {
        if (mode === 'edit' || mode === 'copy' || (id && idNum)) {
            apiCall(
                setLoading,
                appContext,
                () => savedReportApi.getSavedReport(idNum),
                (resp) => {
                    const bean = resp.data as SavedReportBean
                    const mapped = mapSavedReportToFormState(bean, reportType)
                    setSavedReportType(mapped.reportType)
                    form.setFieldsValue(mapped.formValues)
                    form.setFieldValue('period', bean.reportPeriod)
                    form.setFieldValue('timezone', bean.timezoneId)
                    setSelectedKeyColumns(mapped.dimensions)
                    setSelectedValueColumns(mapped.metrics)
                    form.setFieldValue('metrics', mapped.metrics)
                    setSelectedEventColumns(mapped.events)
                    form.setFieldValue('events', mapped.events)
                    setSelectedFilters(mapped.filters)
                    setName(mode === 'copy' ? getCopyName(mapped.name) || '' : mapped.name)
                    autogenFiredRef.current = false
                    setShouldAutogen(!!autoLoadRequested)
                },
            )
        }
    }, [mode, id, idNum, autoLoadRequested])

    const {start, end} = useMemo(() => {
        if (!selectedPeriod) {
            return {start: undefined, end: undefined}
        }
        return resolveDates(selectedPeriod, startDate, endDate, String(selectedTimezone ?? user?.timezoneId ?? 'UTC'))
    }, [selectedPeriod, startDate, endDate, selectedTimezone, user?.timezoneId])

    const createPayload = () =>
        buildReportPayload({
            reportType,
            start,
            end,
            allFilters,
            selectedFilters,
            selectedKeyColumns,
            selectedValueColumns,
            selectedEventColumns,
            reportValues,
            timezone: selectedTimezone,
        })

    const handleGenerate = async () => {
        await generateReport(createPayload(), {keyColumnKeys: selectedKeyColumns})
    }

    const handleReportExport = async (format: 'csv' | 'xlsx') => {
        await form.validateFields()
        await exportReport(createPayload(), format)
    }

  const handleCreateRetargetingList = async ({
    name,
    autoUpdate,
    advertiserId,
  }: CreateRetargetingListFormValues) => {
    if (!user?.advertiserId && !advertiserId) {
      const error = 'Advertiser is required for Retargeting List creation.'
      showError(appContext, error)
      throw new Error(error)
    }

    if (!start || !end) {
      const error = 'Retargeting List requires a valid report period.'
      showError(appContext, error)
      throw new Error(error)
    }

    try {
      await retargetingListApi.create({
        name,
        startDate: start,
        endDate: end,
        autoUpdate,
        reportRequest: createPayload() as any,
        ...(user?.advertiserId ? {} : advertiserId ? { advertiserId } : {}),
      })
      messageApi.success(`Retargeting list "${name}" created`)
      navigateTo(CommonRoutes.RETARGETING_LIST)
    } catch (error) {
      showError(appContext, error)
      throw error
    }
  }

  const onClear = () => {
    form.resetFields()
    form.setFieldsValue({
      period: SavedReportBean.ReportPeriodEnum.TODAY,
      timezone: user?.timezoneId,
    })

    setSelectedKeyColumns([])
    setSelectedValueColumns([])
    setSelectedFilters([{dimension: undefined, value: undefined}])
    clearResults()
  }

    useEffect(() => {
        if (!metadataLoaded) return
        const hasTimezone = selectedTimezone || user?.timezoneId
        const hasSufficientData = selectedValueColumns.length === 0 || reportValues.length >= selectedValueColumns.length
        if (shouldAutogen && start && end && hasTimezone && hasSufficientData) {
            if (autogenFiredRef.current) return
            autogenFiredRef.current = true

      setShouldAutogen(false)
      ;(async () => {
        try {
          await handleGenerate()
        } catch (err) {
          messageApi.error(err)
        }
      })()
    }
  }, [
    metadataLoaded,
    shouldAutogen,
    start,
    end,
    selectedTimezone,
    user?.timezoneId,
    selectedValueColumns.length,
    reportValues.length,
    messageApi,
  ])

    return (
        <Spin spinning={loading}>
            <Card className="report-builder-form-card" style={{borderRadius: 8, marginBottom: 20}}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '24px',
                    }}
                >
                    <CardTitle
                        title={
              name ? (
                            name
              ) : title ? (
                title
              ) : reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker ? (
                <CardTitleWithTooltip
                  title="Tracker Events"
                  tooltip="View and analyze tracker event data for the selected period, columns, and filters."
                />
              ) : (
                <CardTitleWithTooltip
                  title="Report Builder"
                  tooltip="Generate custom reports by selecting metrics, dimensions, and filters. You can also save report configurations to reuse them later."
                />
              )
                        }
                    />
                    <div>
                        <SaveReportButton
                            title={name}
                            modalVisible={modalVisible}
                            setModalVisible={setModalVisible}
                            editMode={mode === 'edit'}
                            saveReport={(title) => {
                                if (mode === 'edit') {
                                    apiCall(
                                        setLoading,
                                        appContext,
                                        () =>
                                            savedReportApi.editSavedReport(
                                                {
                                                    id: idNum,
                                                    ...buildSavedReportBody({
                                                        title,
                                                        reportType,
                                                        selectedPeriod,
                                                        startDate,
                                                        endDate,
                                                        selectedTimezone,
                                                        selectedKeyColumns,
                                                        selectedValueColumns,
                                                        selectedEventColumns,
                                                        selectedFilters,
                                                    }),
                                                } as any,
                                                idNum,
                                            ),
                                        () => {
                                            setName(title)
                                            navigateTo(`${savedReportBaseRoute}/${idNum}/edit`)
                                        },
                                    )
                                } else {
                                    apiCall(
                                        setLoading,
                                        appContext,
                                        () =>
                                            savedReportApi.createSavedReport(
                                                buildSavedReportBody({
                                                    title,
                                                    reportType,
                                                    selectedPeriod,
                                                    startDate,
                                                    endDate,
                                                    selectedTimezone,
                                                    selectedKeyColumns,
                                                    selectedValueColumns,
                                                    selectedEventColumns,
                                                    selectedFilters,
                                                }),
                                            ),
                                        (r) => {
                                            setName(title)
                                            navigateTo(`${savedReportBaseRoute}/${r.data?.id}/edit`)
                                        },
                                    )
                                }
                            }}
                            validate={form.validateFields}
                        />
                        <Button
                            danger
                            ghost
                            style={{width: 115, marginLeft: 8, fontSize: 12}}
                            onClick={onClear}
                            aria-label="Clear form"
                        >
                            <ClearOutlined/> Clear
                        </Button>
                    </div>
                </div>

                <ReportForm
                    loading={reportState.loading}
                    periodLabels={reportPeriodLabels}
                    onGenerate={handleGenerate}
                    allFilters={allFilters}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    selectedKeyColumns={selectedKeyColumns}
                    setSelectedKeyColumns={setSelectedKeyColumns}
                    selectedValueColumns={selectedValueColumns}
                    setSelectedValueColumns={setSelectedValueColumns}
                    selectedEventColumns={selectedEventColumns}
                    setSelectedEventColumns={setSelectedEventColumns}
                    reportKeys={reportKeys}
                    reportValues={reportValues}
                    form={form}
                    reportType={reportType}
                />
            </Card>

      {reportState.hasSearched && (
        <Card>
          {reportState.error && (
            <Alert type="error" showIcon style={{ marginBottom: 12 }} message={reportState.error} />
          )}

          {reportState.warning && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={reportState.warning} />
          )}

          {reportState.columns.length > 0 && (
            <ReportResultTable
              columns={reportState.columns}
              data={rowObjects}
              loading={reportState.loading}
              onSort={changeSort}
              titleText={reportState.date}
              sort={reportState.sort}
              totalValues={reportState.totalValues}
              onExportCsv={() => {
                void handleReportExport('csv')
              }}
              onExportXlsx={() => {
                void handleReportExport('xlsx')
              }}
              onCreateRetargetingList={
                reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker && allowCreateRetargetingList
                  ? handleCreateRetargetingList
                  : undefined
              }
              showAdvertiserSelectorForRetargetingList={!user?.advertiserId}
            />
          )}
        </Card>
      )}
    </Spin>
  )
}

export default Report
